/**
 * Bookings.
 *
 * Same blueprint as Customers, Vendors and Adoption — segments own the status
 * filter, tiles are links that apply their own filter, every filter and sort
 * goes to the server.
 *
 * What makes this module different is that it has a clock. A booking is not just
 * a record in a state; it is an appointment that either has or has not happened
 * yet, and the most useful question in the whole console turns out to be "whose
 * slot has already passed while the booking is still open". Forty-three of the
 * two hundred and twenty-three bookings here are in exactly that state: the
 * vendor never marked them done, so nobody was refunded and nobody was marked
 * no-show. That queue is the default view.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  Copy,
  IndianRupee,
  LifeBuoy,
  MoreHorizontal,
  PawPrint,
  Store,
  Timer,
  Undo2,
  Users,
  User,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ago, date_time, inr, title_case } from '@/lib/format';
import { export_bookings } from '@/features/bookings/api';
import { use_booking_counts, use_booking_list } from '@/features/bookings/hooks';
import {
  BOOKING_SORT_FIELDS,
  DINING_MODE_LABELS,
  BOOKING_STATUS_LABELS,
  BOOKING_STATUS_OPTIONS,
  MODULE_TYPE_OPTIONS,
  type BookingListFilters,
  type BookingListItem,
  type BookingStatus,
  type VendorModuleType,
} from '@/features/bookings/types';
import { VENDOR_MODULE_TYPE_LABELS } from '@/features/vendors/types';

/**
 * Segments, by where a booking sits against its own slot.
 *
 * "Needs attention" is first and is the default, because it is the only one that
 * represents work nobody is doing. The rest are the ordinary passage of an
 * appointment: coming up, happening, done.
 */
const SEGMENTS = ['attention', 'today', 'upcoming', 'live', 'closed', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

/**
 * How long ago a slot ended, said as a duration rather than a date.
 *
 * Only used on the needs-attention queue, where the point is the size of the
 * gap: a booking eight days past its slot and still "confirmed" is a different
 * problem from one that ended an hour ago.
 */
function overdue_label(slot_end: string): string {
  const hours = Math.floor((Date.now() - new Date(slot_end).getTime()) / 3_600_000);
  if (hours < 1) return 'just ended, not closed out';
  if (hours < 24) return `${hours}h overdue`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} overdue`;
}

/** Today, in the same UTC bounds the backend's `today` tile counts. */
function today_iso(): string {
  return new Date().toISOString().slice(0, 10);
}

function segment_filters(segment: Segment): Partial<BookingListFilters> {
  switch (segment) {
    case 'attention':
      return { needs_attention: 'true' };
    case 'today':
      return { slot_from: today_iso(), slot_to: today_iso() };
    case 'upcoming':
      // Everything still open from today onward — the forward-looking book.
      return { statuses: 'AWAITING_VENDOR,CONFIRMED', slot_from: today_iso() };
    case 'live':
      return { statuses: 'IN_PROGRESS' };
    case 'closed':
      return { statuses: 'COMPLETED,CANCELLED,NO_SHOW' };
    case 'all':
    default:
      return {};
  }
}

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  attention: {
    label: 'Needs attention',
    title: 'Bookings needing attention',
    blurb:
      'The slot has passed but nobody closed the booking out — no completion, no refund, no no-show',
  },
  today: {
    label: 'Today',
    title: "Today's bookings",
    blurb: 'Everything scheduled between midnight and midnight, whatever its state',
  },
  upcoming: {
    label: 'Upcoming',
    title: 'Upcoming bookings',
    blurb: 'Still open and still ahead of us — the forward book',
  },
  live: {
    label: 'In progress',
    title: 'Bookings in progress',
    blurb: 'A vendor has started these and not yet finished them',
  },
  closed: {
    label: 'Closed',
    title: 'Closed bookings',
    blurb: 'Completed, cancelled, or marked as a no-show',
  },
  all: { label: 'All', title: 'Bookings', blurb: 'Every booking ever taken' },
};

/** One colour per status, used on both the list and the detail page. */
export const BOOKING_STATUS_TONE: Record<
  BookingStatus,
  'success' | 'warning' | 'neutral' | 'destructive' | 'info' | 'primary'
> = {
  PENDING_PAYMENT: 'warning',
  AWAITING_VENDOR: 'warning',
  CONFIRMED: 'info',
  IN_PROGRESS: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  NO_SHOW: 'destructive',
};

const PAYMENT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'destructive'> = {
  CAPTURED: 'success',
  PENDING: 'warning',
  REFUNDED: 'destructive',
  FAILED: 'destructive',
};

const PAYMENT_STATUS_OPTIONS = ['PENDING', 'CAPTURED', 'REFUNDED', 'FAILED'] as const;

export function BookingListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'attention';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [status, set_status] = use_url_state('status', enum_param(BOOKING_STATUS_OPTIONS));
  const [module_type, set_module_type] = use_url_state(
    'module_type',
    enum_param(MODULE_TYPE_OPTIONS),
  );
  const [payment_status, set_payment_status] = use_url_state(
    'payment_status',
    enum_param(PAYMENT_STATUS_OPTIONS),
  );
  const [has_incident, set_has_incident] = use_url_state('has_incident', bool_param);
  const [has_support, set_has_support] = use_url_state('has_support_request', bool_param);
  const [needs_attention, set_needs_attention] = use_url_state('needs_attention', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    // Slot order, not creation order: this is a diary, and the first thing an
    // operator wants is the oldest unfinished appointment.
    sort_param(BOOKING_SORT_FIELDS, { by: 'slot_start', dir: 'desc' }),
  );

  const reset_page = () => set_page(1);

  const seg = segment_filters(segment);

  const filters: BookingListFilters = useMemo(
    () => ({
      ...seg,
      page,
      page_size,
      q: q || undefined,
      // A status picked by hand beats the segment's set: an operator narrowing
      // "Closed" to "No show" means it, and quietly ignoring them is how a
      // filter ends up looking alive while doing nothing.
      ...(status ? { statuses: undefined, status } : {}),
      module_type: module_type || undefined,
      payment_status: payment_status || undefined,
      has_incident: has_incident || undefined,
      has_support_request: has_support || undefined,
      // The segment owns this one; a hand-set value only applies elsewhere.
      needs_attention: segment === 'attention' ? 'true' : needs_attention || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [
      seg,
      segment,
      page,
      page_size,
      q,
      status,
      module_type,
      payment_status,
      has_incident,
      has_support,
      needs_attention,
      from,
      to,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = use_booking_list(filters, sort);
  const { data: counts } = use_booking_counts();

  /* --------------------------------- chips --------------------------------- */

  const bool_chip = (
    key: string,
    label: string,
    value: string,
    yes: string,
    no: string,
    clear: () => void,
  ) =>
    value
      ? {
          key,
          label,
          value: value === 'true' ? yes : no,
          advanced: true,
          on_clear: () => {
            clear();
            reset_page();
          },
        }
      : null;

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    status && {
      key: 'status',
      label: 'Status',
      value: BOOKING_STATUS_LABELS[status],
      on_clear: () => set_status(''),
    },
    module_type && {
      key: 'module_type',
      label: 'Module',
      value: VENDOR_MODULE_TYPE_LABELS[module_type] ?? module_type,
      on_clear: () => set_module_type(''),
    },
    payment_status && {
      key: 'payment_status',
      label: 'Payment',
      value: title_case(payment_status),
      advanced: true,
      on_clear: () => set_payment_status(''),
    },
    bool_chip('has_incident', 'Incidents', has_incident, 'Reported', 'None', () =>
      set_has_incident(''),
    ),
    bool_chip('has_support_request', 'Support', has_support, 'Raised', 'None', () =>
      set_has_support(''),
    ),
    segment === 'attention'
      ? null
      : bool_chip(
          'needs_attention',
          'Slot passed',
          needs_attention,
          'Still open',
          'Closed out',
          () => set_needs_attention(''),
        ),
    (from || to) && {
      key: 'date',
      label: 'Booked',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_status('');
    set_module_type('');
    set_payment_status('');
    set_has_incident('');
    set_has_support('');
    set_needs_attention('');
    set_from('');
    set_to('');
    reset_page();
  }

  /* -------------------------------- metrics -------------------------------- */

  const metrics: Metric[] = useMemo(
    () => [
      {
        key: 'needs_attention',
        label: 'Needs attention',
        value: counts?.needs_attention?.toLocaleString('en-IN'),
        hint: 'Slot passed, still open',
        // The only tile that represents work nobody is doing.
        tone: counts && counts.needs_attention > 0 ? 'warning' : 'default',
        to: '/bookings?segment=attention',
        icon: AlertTriangle,
        loading: !counts,
      },
      {
        key: 'awaiting_vendor',
        label: 'Awaiting vendor',
        value: counts?.awaiting_vendor?.toLocaleString('en-IN'),
        hint: 'Nobody has accepted yet',
        tone: counts && counts.awaiting_vendor > 0 ? 'warning' : 'default',
        to: '/bookings?segment=all&status=AWAITING_VENDOR',
        icon: Timer,
        loading: !counts,
      },
      {
        key: 'today',
        label: 'Today',
        value: counts?.today?.toLocaleString('en-IN'),
        to: '/bookings?segment=today',
        icon: CalendarDays,
        loading: !counts,
      },
      {
        key: 'open_incidents',
        label: 'Open incidents',
        value: counts?.open_incidents?.toLocaleString('en-IN'),
        hint: 'Something went wrong on a walk',
        tone: counts && counts.open_incidents > 0 ? 'destructive' : 'default',
        to: '/bookings?segment=all&has_incident=true',
        icon: LifeBuoy,
        loading: !counts,
      },
      {
        key: 'captured',
        label: 'Taken',
        value: counts ? inr(counts.captured_inr) : undefined,
        hint: counts ? `${inr(counts.refunded_inr)} refunded` : undefined,
        to: '/bookings?segment=all&payment_status=CAPTURED',
        icon: IndianRupee,
        loading: !counts,
      },
    ],
    [counts],
  );

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<BookingListItem>[]>(
    () => [
      {
        id: 'slot_start',
        header: 'Slot',
        meta: { sort_key: 'slot_start' },
        cell: ({ row }) => {
          const b = row.original;
          return (
            <span className="flex min-w-0 flex-col">
              <span className="whitespace-nowrap font-semibold">{date_time(b.slot_start)}</span>
              <span className="truncate text-micro text-muted-foreground">
                {/*
                  How overdue, not when — the absolute date is already on the line
                  above, and `ago()` falls back to printing it again for anything
                  more than a few days old, which read as the same date twice.
                  "8 days overdue" is the number that makes a still-open booking
                  obviously wrong without the operator doing the arithmetic.
                */}
                {b.needs_attention ? (
                  <span className="text-warning">{overdue_label(b.slot_end)}</span>
                ) : (
                  ago(b.slot_start)
                )}
              </span>
            </span>
          );
        },
      },
      {
        id: 'what',
        header: 'What',
        meta: { sort_key: 'module_type' },
        cell: ({ row }) => {
          const b = row.original;
          return (
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{b.service_name ?? 'Service removed'}</span>
              <span className="flex flex-wrap items-center gap-1 text-micro text-muted-foreground">
                <Badge tone="neutral" size="sm">
                  {/*
                    For a café or restaurant, the mode is the useful label — a
                    table reservation and an event ticket are different jobs, and
                    "Restaurant" told an operator neither.
                  */}
                  {b.dining_offering
                    ? DINING_MODE_LABELS[b.dining_offering.mode]
                    : (VENDOR_MODULE_TYPE_LABELS[b.module_type] ?? b.module_type)}
                </Badge>
                {/*
                  Dining leads with the party, not the pet: these bookings are
                  people at a table and almost none of them carry an animal.
                */}
                {b.party_size !== null ? (
                  <span className="inline-flex items-center gap-1">
                    <Users aria-hidden="true" className="h-3 w-3" />
                    {b.party_size} {b.party_size === 1 ? 'guest' : 'guests'}
                  </span>
                ) : (
                  b.pet && (
                    <span className="inline-flex items-center gap-1">
                      <PawPrint aria-hidden="true" className="h-3 w-3" />
                      {b.pet.name}
                    </span>
                  )
                )}
              </span>
            </span>
          );
        },
      },
      {
        id: 'vendor',
        header: 'Vendor',
        enableSorting: false,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <Link
              to={`/vendors/${b.vendor_profile_id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex min-w-0 items-center gap-1.5 truncate underline-offset-2 hover:text-primary hover:underline"
            >
              <Store aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{b.vendor_business_name ?? 'Vendor removed'}</span>
            </Link>
          );
        },
      },
      {
        id: 'customer',
        header: 'Customer',
        enableSorting: false,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <PersonAvatar path={null} name={b.user_name} seed={b.user_id} size="sm" />
              <span className="flex min-w-0 flex-col">
                <Link
                  to={`/users/${b.user_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  {b.user_name ?? 'No name on file'}
                </Link>
                <span className="truncate text-micro text-muted-foreground">
                  {b.user_phone ?? '—'}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'price_inr',
        accessorKey: 'price_inr',
        header: 'Price',
        meta: { align: 'right', sort_key: 'price_inr' },
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular font-semibold">
            {inr(row.original.price_inr)}
          </span>
        ),
      },
      {
        id: 'payment_status',
        header: 'Payment',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original.payment_status;
          if (!p) return <span className="text-muted-foreground/60">None</span>;
          return (
            <Badge tone={PAYMENT_TONE[p] ?? 'neutral'} size="sm" dot>
              {title_case(p)}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sort_key: 'status' },
        cell: ({ row }) => {
          const b = row.original;
          return (
            <span className="flex flex-wrap items-center gap-1">
              <Badge
                tone={BOOKING_STATUS_TONE[b.status]}
                size="sm"
                dot
                data-testid={`status-${b.status}`}
              >
                {BOOKING_STATUS_LABELS[b.status]}
              </Badge>
              {/*
                Trouble markers. Both were invisible on this list, so "which
                booking is the complaint about" meant opening records one by one.
              */}
              {b.open_incident_count > 0 && (
                <Badge tone="destructive" size="sm" dot data-testid="badge-incident">
                  {b.open_incident_count} incident{b.open_incident_count === 1 ? '' : 's'}
                </Badge>
              )}
              {b.support_request_count > 0 && (
                <Badge tone="warning" size="sm" data-testid="badge-support">
                  {b.support_request_count} support
                </Badge>
              )}
              {b.reschedule_count > 2 && (
                <Badge
                  tone="neutral"
                  size="sm"
                  // Without this the icon and the count break onto two lines
                  // inside the badge and it reads as a rendering fault.
                  className="whitespace-nowrap"
                  title={`Status changed ${b.reschedule_count} times`}
                >
                  <Undo2 className="h-3 w-3" /> {b.reschedule_count}
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const b = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Booking actions"
                  data-testid={`row-actions-${b.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(b.id);
                    notify.success('Booking ID copied');
                  }}
                  testid={`copy-${b.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy booking ID
                </RowAction>
                <DropdownMenuSeparator />
                {/*
                  The three places an operator goes next. Bookings themselves are
                  read-only here on purpose — cancelling and refunding belong to
                  the support-request flow, which records who asked and why.
                */}
                <RowAction
                  on_select={() => window.open(`/vendors/${b.vendor_profile_id}`, '_self')}
                  testid={`open-vendor-${b.id}`}
                >
                  <Store className="h-4 w-4" /> Open the vendor
                </RowAction>
                <RowAction
                  on_select={() => window.open(`/users/${b.user_id}`, '_self')}
                  testid={`open-customer-${b.id}`}
                >
                  <User className="h-4 w-4" /> Open the customer
                </RowAction>
                {b.support_request_count > 0 && (
                  <RowAction
                    on_select={() => window.open(`/support-requests?q=${b.id}`, '_self')}
                    testid={`open-support-${b.id}`}
                  >
                    <LifeBuoy className="h-4 w-4" /> Support requests
                  </RowAction>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`bookings:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            // The one queue worth carrying a number on the tab itself.
            count: v === 'attention' ? counts?.needs_attention : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<BookingListItem>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(b) => b.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(b) => `/bookings/${b.id}`}
        storage_key={`bookings:${segment}`}
        export_name="bookings"
        on_export_all={() => export_bookings(filters, sort)}
        caption="Bookings"
        test_id="booking-list"
        selectable
        empty={{
          icon: CalendarClock,
          title: chips.some(Boolean)
            ? 'No bookings match these filters'
            : segment === 'attention'
              ? 'Nothing is stuck'
              : `No ${copy.label.toLowerCase()} bookings`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : segment === 'attention'
              ? 'Every booking whose slot has passed has been completed, cancelled or marked no-show.'
              : 'Bookings appear here as customers book services in the app.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SelectFilter
                  label="Status"
                  value={status}
                  on_change={(v) => {
                    set_status(v as BookingStatus);
                    reset_page();
                  }}
                  options={BOOKING_STATUS_OPTIONS.map((s) => ({
                    value: s,
                    label: BOOKING_STATUS_LABELS[s],
                  }))}
                  all_label="Any status"
                  test_id="filter-status"
                />
                <SelectFilter
                  label="Payment"
                  value={payment_status}
                  on_change={(v) => {
                    set_payment_status(v);
                    reset_page();
                  }}
                  options={PAYMENT_STATUS_OPTIONS.map((s) => ({ value: s, label: title_case(s) }))}
                  all_label="Any payment"
                  test_id="filter-payment-status"
                />
                <BoolFilter
                  label="Incidents"
                  value={has_incident}
                  on_change={(v) => {
                    set_has_incident(v);
                    reset_page();
                  }}
                  true_label="Reported"
                  false_label="None"
                  test_id="filter-has-incident"
                />
                <BoolFilter
                  label="Support requests"
                  value={has_support}
                  on_change={(v) => {
                    set_has_support(v);
                    reset_page();
                  }}
                  true_label="Raised"
                  false_label="None"
                  test_id="filter-has-support"
                />
                <BoolFilter
                  label="Slot passed"
                  value={segment === 'attention' ? 'true' : needs_attention}
                  on_change={(v) => {
                    set_needs_attention(v);
                    reset_page();
                  }}
                  true_label="Still open"
                  false_label="Closed out"
                  test_id="filter-needs-attention"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Customer, vendor, service, pet or booking ID…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Module"
              value={module_type}
              on_change={(v) => {
                set_module_type(v as VendorModuleType);
                reset_page();
              }}
              options={MODULE_TYPE_OPTIONS.map((m) => ({
                value: m,
                label: VENDOR_MODULE_TYPE_LABELS[m] ?? m,
              }))}
              all_label="Any module"
              test_id="filter-module"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Booked"
            />
          </FilterBar>
        }
      />
    </DashboardLayout>
  );
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}

export default BookingListPage;
